import { useNavigate, Link } from "react-router-dom";
import { FileText, Sparkles, Zap, Shield, Users, ArrowRight, ChevronRight, BarChart3, Globe, Check, Rocket, Building2 } from "lucide-react";

const FEATURES = [
  { icon: Sparkles, title: "IA Inteligente", desc: "Genera diseños y contenido personalizado para cada cliente con inteligencia artificial." },
  { icon: Zap, title: "En Minutos", desc: "Crea cotizaciones profesionales en minutos, no en horas. Automatiza tu proceso de ventas." },
  { icon: Globe, title: "Análisis de Clientes", desc: "La IA analiza el sitio web de tu cliente para personalizar cada propuesta automáticamente." },
  { icon: Shield, title: "PDF Profesional", desc: "Genera PDFs de alta calidad con tu marca, listos para enviar y cerrar negocios." },
  { icon: Users, title: "Pipeline CRM", desc: "Kanban con etapas personalizables, versionado e historial completo de cambios." },
  { icon: BarChart3, title: "Cotflow Insights", desc: "Chat con IA que analiza tu negocio: márgenes, conversión, clientes top y tendencias." },
];

const STEPS = [
  { num: "01", title: "Configura tu empresa", desc: "Sube tu logo, define tus servicios y precios base." },
  { num: "02", title: "Agrega clientes", desc: "Registra sus datos y deja que la IA investigue su negocio." },
  { num: "03", title: "Crea la cotización", desc: "Selecciona servicios, descuentos y regalías. La IA genera el diseño." },
  { num: "04", title: "Descarga y envía", desc: "Obtén un PDF profesional listo para cerrar el negocio." },
];

const PRICING = [
  {
    id: "free",
    label: "Free",
    price: "$0",
    priceSuffix: "para siempre",
    icon: Sparkles,
    color: "text-slate-600",
    ring: "border-slate-200",
    bullets: [
      "3 cotizaciones por mes",
      "10 clientes · 3 productos",
      "1 template propio",
      "Generación IA de cotizaciones",
    ],
  },
  {
    id: "pro",
    label: "Pro",
    price: "$9.990",
    priceSuffix: "CLP / mes",
    icon: Rocket,
    color: "text-blue-600",
    ring: "border-blue-500 ring-2 ring-blue-100",
    highlight: "Más popular",
    bullets: [
      "Cotizaciones ilimitadas",
      "Clientes y productos ilimitados",
      "Pipeline CRM Kanban completo",
      "Análisis IA de clientes",
      "Versionado y secciones extra",
    ],
  },
  {
    id: "empresa",
    label: "Empresa",
    price: "$14.990",
    priceSuffix: "CLP / mes",
    icon: Building2,
    color: "text-violet-600",
    ring: "border-violet-200",
    bullets: [
      "Todo lo de Pro",
      "3 usuarios incluidos",
      "Roles Admin / Supervisor / Usuario",
      "Reasignación de responsables",
      "Filtros por rol en Pipeline",
    ],
  },
];

export default function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 h-16">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="CotflowAI" className="w-8 h-8 object-contain" />
            <span className="font-black text-lg tracking-tight font-['Satoshi']" data-testid="home-logo">CotflowAI</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
            <a href="#features" className="hover:text-slate-900 transition-colors">Funcionalidades</a>
            <a href="#how" className="hover:text-slate-900 transition-colors">Cómo funciona</a>
            <a href="#pricing" className="hover:text-slate-900 transition-colors">Planes</a>
          </div>
          <button onClick={() => navigate("/auth")} className="bg-slate-900 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-slate-800 transition-all active:scale-95" data-testid="home-login-btn">
            Iniciar sesión
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6" data-testid="hero-section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-full mb-8">
            <Sparkles size={14} />
            Potenciado por Inteligencia Artificial
          </div>
          <h1 className="text-5xl md:text-7xl font-black font-['Satoshi'] tracking-tight text-slate-900 leading-[1.05] mb-6">
            Cotizaciones<br />
            <span className="text-blue-600">profesionales</span><br />
            en minutos
          </h1>
          <p className="text-lg md:text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed mb-10">
            Crea, personaliza y envía cotizaciones con inteligencia artificial.
            Tu empresa, tus servicios, tu marca. Sin plantillas genéricas.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button onClick={() => navigate("/auth")} className="bg-blue-600 text-white font-bold px-8 py-4 rounded-xl text-base hover:bg-blue-700 transition-all active:scale-95 shadow-lg shadow-blue-600/20 flex items-center gap-2 group" data-testid="hero-cta">
              Comienza Gratis
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
            <span className="text-sm text-slate-400">Sin tarjeta de crédito</span>
          </div>
        </div>
      </section>

      {/* Social proof bar */}
      <section className="border-y border-slate-100 bg-slate-50/50 py-8 px-6">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-x-12 gap-y-4 text-slate-400 text-sm font-medium">
          <span>Cotizaciones generadas al instante</span>
          <span className="w-1 h-1 bg-slate-300 rounded-full hidden sm:block" />
          <span>PDFs con tu marca</span>
          <span className="w-1 h-1 bg-slate-300 rounded-full hidden sm:block" />
          <span>IA que conoce a tu cliente</span>
          <span className="w-1 h-1 bg-slate-300 rounded-full hidden sm:block" />
          <span>Multi-moneda (CLP, UF, USD, EUR)</span>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 px-6" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-wider text-blue-600 mb-3 block">Funcionalidades</span>
            <h2 className="text-3xl md:text-4xl font-black font-['Satoshi'] tracking-tight text-slate-900">Todo lo que necesitas para cotizar</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map(({ icon: Icon, title, desc }, i) => (
              <div key={i} className="group bg-white rounded-2xl border border-slate-200 p-7 hover:border-blue-200 hover:shadow-lg hover:shadow-blue-500/5 transition-all duration-300" data-testid={`feature-${i}`}>
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-5 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                  <Icon size={22} className="text-blue-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-bold text-lg text-slate-900 mb-2">{title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-24 px-6 bg-slate-50/80" data-testid="how-it-works-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-wider text-blue-600 mb-3 block">Proceso</span>
            <h2 className="text-3xl md:text-4xl font-black font-['Satoshi'] tracking-tight text-slate-900">Así de simple</h2>
          </div>
          <div className="space-y-6">
            {STEPS.map(({ num, title, desc }, i) => (
              <div key={i} className="flex items-start gap-6 bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-md transition-all" data-testid={`step-how-${i}`}>
                <div className="text-3xl font-black text-blue-100 font-['Satoshi'] flex-shrink-0 w-14">{num}</div>
                <div>
                  <h3 className="font-bold text-lg text-slate-900 mb-1">{title}</h3>
                  <p className="text-sm text-slate-500">{desc}</p>
                </div>
                {i < STEPS.length - 1 && <ChevronRight size={20} className="text-slate-300 ml-auto flex-shrink-0 mt-2" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 px-6" data-testid="pricing-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-wider text-blue-600 mb-3 block">Planes</span>
            <h2 className="text-3xl md:text-4xl font-black font-['Satoshi'] tracking-tight text-slate-900">Empieza gratis, escala cuando quieras</h2>
            <p className="text-slate-500 mt-4 max-w-xl mx-auto">Sin sorpresas. Cancela o cambia de plan cuando lo necesites.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {PRICING.map((pl) => {
              const Icon = pl.icon;
              return (
                <div key={pl.id} className={`relative bg-white rounded-2xl border ${pl.ring} p-7 flex flex-col`} data-testid={`pricing-${pl.id}`}>
                  {pl.highlight && (
                    <span className="absolute -top-3 left-6 bg-blue-600 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">{pl.highlight}</span>
                  )}
                  <Icon size={24} className={pl.color} />
                  <h3 className="text-2xl font-black text-slate-900 mt-3">{pl.label}</h3>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="text-4xl font-black text-slate-900">{pl.price}</span>
                    <span className="text-sm text-slate-500">{pl.priceSuffix}</span>
                  </div>
                  <ul className="mt-5 space-y-2.5 flex-1">
                    {pl.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <button
              onClick={() => navigate("/auth")}
              className="bg-blue-600 text-white font-bold px-10 py-4 rounded-xl text-base hover:bg-blue-700 shadow-lg shadow-blue-600/20 transition-all active:scale-95"
              data-testid="pricing-cta-main"
            >
              Comienza Gratis
            </button>
            <p className="text-xs text-slate-400 mt-3">Todas las cuentas parten gratis. Actualiza a Pro o Empresa cuando quieras desde la app.</p>
          </div>
          <p className="text-center text-xs text-slate-400 mt-8">
            Pagos por transferencia bancaria. Al solicitar un plan desde la app, recibirás las instrucciones de pago en tu correo.
            Dudas a <a href="mailto:pagos@cotflowai.cl" className="text-blue-600 underline">pagos@cotflowai.cl</a>.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <div className="bg-slate-900 rounded-3xl p-12 md:p-16 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/15 to-transparent" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-black font-['Satoshi'] tracking-tight text-white mb-4">
                Deja de perder tiempo con cotizaciones manuales
              </h2>
              <p className="text-slate-400 text-lg mb-8 max-w-xl mx-auto">
                Únete y transforma la forma en que generas propuestas comerciales.
              </p>
              <button onClick={() => navigate("/auth")} className="bg-blue-600 text-white font-bold px-8 py-4 rounded-xl text-base hover:bg-blue-500 transition-all active:scale-95 shadow-lg shadow-blue-600/30 flex items-center gap-2 mx-auto group" data-testid="cta-button">
                Comienza Gratis
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-12 px-6 bg-slate-50/50">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2.5 mb-2">
                <img src="/logo.png" alt="CotflowAI" className="w-7 h-7 object-contain" />
                <span className="font-black text-base font-['Satoshi']">CotflowAI</span>
              </div>
              <p className="text-sm text-slate-500 max-w-xs">Cotizaciones inteligentes para empresas inteligentes.</p>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-500">
              <a href="#pricing" className="hover:text-slate-900 transition-colors">Planes</a>
              <a href="#features" className="hover:text-slate-900 transition-colors">Funcionalidades</a>
              <Link to="/legal/terms" className="hover:text-slate-900 transition-colors" data-testid="footer-terms">Términos y Condiciones</Link>
              <Link to="/legal/privacy" className="hover:text-slate-900 transition-colors" data-testid="footer-privacy">Privacidad</Link>
              <a href="mailto:contacto@auttobots.com" className="hover:text-slate-900 transition-colors">Contacto</a>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
            <span>© {new Date().getFullYear()} CotflowAI. Todos los derechos reservados.</span>
            <span>Hecho en Chile</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
